import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { DUBAI_ZONE, todayDubaiYMD } from '../../../utils/dubaiTime';

/* Papers and upkeep (2026-11-03). How close each date or service is comes
   from the server, so this page and the 08:00 bell (vehicleAlerts.js) agree;
   these are the same thresholds, for colour only. */
const SOON_DAYS = 30;
const SOON_KM = 1000;

const DOCS = [
    { type: 'mulkiya_front', label: 'Mulkiya — front' },
    { type: 'mulkiya_back', label: 'Mulkiya — back' },
    { type: 'insurance', label: 'Insurance' },
];

const UPKEEP = [
    'registration_expiry', 'insurance_expiry', 'insurance_provider',
    'last_service_date', 'service_interval_km', 'last_service_km', 'battery_changed_on',
];

const km = (n) => `${Number(n).toLocaleString('en-US')} km`;
const fmtDay = (ymd) => (ymd ? DateTime.fromISO(ymd, { zone: DUBAI_ZONE }).toFormat('d LLL yyyy') : '');
const dayTone = (d) => (d == null ? '' : d < 0 ? 'is-overdue' : d <= SOON_DAYS ? 'is-soon' : 'is-ok');
const dayLabel = (d) => (d < 0 ? `Expired ${-d}d ago` : d === 0 ? 'Expires today' : `${d}d left`);
const kmTone = (k) => (k == null ? '' : k <= 0 ? 'is-overdue' : k <= SOON_KM ? 'is-soon' : 'is-ok');
const kmLabel = (k) => (k < 0 ? `${km(-k)} over` : k === 0 ? 'Due now' : `${km(k)} to go`);

/** "1 yr 8 mo ago" — how old the battery is. */
const ageLabel = (ymd) => {
    const from = DateTime.fromISO(ymd, { zone: DUBAI_ZONE }).startOf('day');
    const now = DateTime.now().setZone(DUBAI_ZONE).startOf('day');
    const { years, months } = now.diff(from, ['years', 'months']).toObject();
    const y = Math.floor(years || 0);
    const m = Math.floor(months || 0);
    if (!y && !m) return 'this month';
    return `${y ? `${y} yr ` : ''}${m ? `${m} mo ` : ''}ago`;
};

/** What needs doing across the fleet, most urgent first. */
const attentionOf = (vehicles) => {
    const out = [];
    for (const v of vehicles.filter((x) => x.is_active)) {
        const paper = (label, d) => {
            if (d == null || d > SOON_DAYS) return;
            out.push({ key: `${v.id}-${label}`, v, tone: dayTone(d), rank: d,
                text: d < 0 ? `${label} expired ${-d}d ago` : d === 0 ? `${label} expires today` : `${label} expires in ${d}d` });
        };
        paper('Mulkiya', v.registration_days_left);
        paper('Insurance', v.insurance_days_left);
        const k = v.service_km_left;
        if (k != null && k <= SOON_KM) {
            out.push({ key: `${v.id}-service`, v, tone: kmTone(k), rank: k <= 0 ? -1 : 0,
                text: k <= 0 ? `Service ${k < 0 ? `${km(-k)} overdue` : 'due now'}` : `Service in ${km(k)}` });
        }
    }
    return out.sort((a, b) => a.rank - b.rank);
};

/**
 * The fleet, and what its meters say.
 *
 * 2026-07-31.
 *
 * THE READING IS EDITED WHERE IT WAS TYPED. A vehicle's current odometer is not
 * a field on the vehicle — it is the latest reading a driver gave it on a
 * shift. So correcting a mistake edits that shift, not the car, and everything
 * derived from it (distance driven, the next clock-on's "cannot go backwards"
 * check) corrects itself. A number stored on the car as well would be a second
 * version of the truth, and it is always the copy that drifts.
 *
 * ARCHIVE, NEVER DELETE. A sold van is still part of last month's mileage and
 * still named by the shifts that used it.
 */
export default function Vehicles() {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    /* null = closed, {} = adding, {id,...} = editing */
    const [editing, setEditing] = useState(null);
    const [formError, setFormError] = useState('');
    /* doc type → { state: 'uploading' | 'error', message } */
    const [docStatus, setDocStatus] = useState({});
    const fileInputs = useRef({});
    const [reading, setReading] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/routing/vehicles');
            setVehicles(res.data?.vehicles || []);
            setError('');
        } catch {
            setError('Could not load the vehicles.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // While a dialog is open the page behind it must not scroll — on a phone
    // a swipe inside the dialog used to drag the page instead, and the dialog
    // (which had no scrolling of its own) stayed put with its newest rows cut
    // off above the screen (owner 2026-10-05).
    const dialogOpen = !!editing || !!reading;
    useEffect(() => {
        if (!dialogOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [dialogOpen]);

    const say = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

    const openEditor = (v) => {
        setFormError('');
        setDocStatus({});
        setEditing(v ? { ...v } : { name: '' });
    };

    const field = (key, value) => setEditing((ed) => ({ ...ed, [key]: value }));

    /* A new service date fills "meter at service" from the drivers' readings
       that day — unless somebody has already typed the sticker figure. */
    const setServiceDate = async (ymd) => {
        setEditing((ed) => ({ ...ed, last_service_date: ymd }));
        if (!ymd || !editing?.id || editing.kmTyped) return;
        try {
            const res = await axios.get(`/api/routing/vehicles/${editing.id}/meter`, { params: { date: ymd } });
            setEditing((ed) => (ed && !ed.kmTyped && ed.last_service_date === ymd
                ? { ...ed, last_service_km: res.data?.km ?? '', meterSource: res.data?.source || null }
                : ed));
        } catch { /* the field stays as it is; the server fills it on save */ }
    };

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (editing.last_service_date && !editing.service_interval_km) {
            setFormError('Choose a 5,000 km or a 10,000 km service.');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const body = {
                name: editing.name,
                plate: editing.plate || null,
                note: editing.note || null,
            };
            for (const k of UPKEEP) body[k] = editing[k] ?? null;
            if (editing.id) {
                await axios.put(`/api/routing/vehicles/${editing.id}`, body);
                say(`${editing.name} updated.`);
            } else {
                await axios.post('/api/routing/vehicles', body);
                say(`${editing.name} added.`);
            }
            setEditing(null);
            await load();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Could not save that vehicle.');
        } finally {
            setSaving(false);
        }
    };

    /* Same storage flow as staff documents: presign, PUT the file, record it.
       Saved straight away — it does not wait for the form's Save. */
    const upload = async (docType, file) => {
        if (!file || !editing?.id) return;
        const vehicleId = editing.id;
        setDocStatus((s) => ({ ...s, [docType]: { state: 'uploading' } }));
        try {
            const contentType = file.type || 'application/octet-stream';
            const presign = await axios.post(`/api/routing/vehicles/${vehicleId}/documents/presign`, {
                doc_type: docType, content_type: contentType,
            });
            await axios.put(presign.data.url, file, {
                // Storage CORS has no Allow-Credentials: the global
                // withCredentials default would make the browser block this PUT.
                withCredentials: false,
                headers: { 'Content-Type': contentType, 'x-amz-acl': 'public-read' },
            });
            const rec = await axios.post(`/api/routing/vehicles/${vehicleId}/documents`, {
                doc_type: docType, file_key: presign.data.fileKey,
            });
            setEditing((ed) => (ed && ed.id === vehicleId
                ? { ...ed, documents: { ...(ed.documents || {}), [docType]: rec.data.document } }
                : ed));
            setDocStatus((s) => ({ ...s, [docType]: null }));
            load();
        } catch (err) {
            setDocStatus((s) => ({
                ...s,
                [docType]: { state: 'error', message: err.response?.data?.error || 'Upload failed. Try again.' },
            }));
        }
    };

    const setArchived = async (v, archived) => {
        try {
            const res = await axios.put(`/api/routing/vehicles/${v.id}/archived`, { archived });
            const n = res.data?.shifts || 0;
            say(archived
                ? `${v.name} archived${n ? ` — its ${n} recorded shift${n === 1 ? '' : 's'} stay${n === 1 ? 's' : ''} in the history.` : '.'}`
                : `${v.name} is back on the drivers' list.`);
            await load();
        } catch (err) {
            setError(err.response?.data?.error || 'Could not archive that vehicle.');
        }
    };

    /* THE CORRECTION DIALOG LISTS THE RECENT SHIFTS (owner, 2026-09-08). An
       extra zero typed at clock-off gets copied into the next morning's
       clock-on (the app refuses anything lower) and extended that evening —
       three wrong numbers on two shifts. A dialog editing only "the last
       reading" could not repair that; this one shows every recent clock-on
       and clock-off, each editable, and the server says which neighbour has
       to be corrected first when the numbers would run backwards. */
    const openReadings = async (v) => {
        setError('');
        try {
            const res = await axios.get(`/api/routing/vehicles/${v.id}/readings`);
            const shifts = res.data?.shifts || [];
            setReading({
                vehicleId: v.id,
                vehicle: v.name,
                shifts,
                edits: Object.fromEntries(shifts.map((s) => [s.id, {
                    start: s.start_odometer != null ? String(s.start_odometer) : '',
                    end: s.end_odometer != null ? String(s.end_odometer) : '',
                }])),
                rowError: {},
            });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not load the readings.');
        }
    };

    const editRow = (shiftId, field, value) => setReading((r) => ({
        ...r,
        edits: { ...r.edits, [shiftId]: { ...r.edits[shiftId], [field]: value.replace(/\D/g, '') } },
        rowError: { ...r.rowError, [shiftId]: '' },
    }));

    const rowDirty = (s) => {
        const e = reading?.edits?.[s.id];
        if (!e) return false;
        return e.start !== String(s.start_odometer ?? '') || e.end !== String(s.end_odometer ?? '');
    };

    const saveRow = async (s) => {
        if (saving) return;
        const e = reading.edits[s.id];
        setSaving(true);
        try {
            const body = {};
            if (e.start !== String(s.start_odometer ?? '')) body.start_odometer = e.start;
            if (s.end_odometer != null && e.end !== String(s.end_odometer)) body.end_odometer = e.end;
            await axios.put(`/api/routing/vehicles/readings/${s.id}`, body);
            say(`${reading.vehicle} corrected.`);
            const fresh = await axios.get(`/api/routing/vehicles/${reading.vehicleId}/readings`);
            const shifts = fresh.data?.shifts || [];
            setReading((r) => ({
                ...r,
                shifts,
                edits: Object.fromEntries(shifts.map((x) => [x.id, {
                    start: x.start_odometer != null ? String(x.start_odometer) : '',
                    end: x.end_odometer != null ? String(x.end_odometer) : '',
                }])),
                rowError: {},
            }));
            await load();
        } catch (err) {
            setReading((r) => ({
                ...r,
                rowError: { ...r.rowError, [s.id]: err.response?.data?.error || 'Could not correct that reading.' },
            }));
        } finally {
            setSaving(false);
        }
    };

    const dayOf = (ts) => (ts
        ? new Date(ts).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' })
        : '—');

    const shown = vehicles.filter((v) => (showArchived ? true : v.is_active));
    const attention = attentionOf(vehicles);

    return (
        <div className="veh-wrap">
            {error ? <div className="veh-error" onClick={() => setError('')}>{error}</div> : null}
            {notice ? <div className="veh-notice">{notice}</div> : null}

            <div className="veh-head">
                <button type="button" className="veh-add" onClick={() => openEditor(null)}>
                    + Add a vehicle
                </button>
                <label className="veh-toggle">
                    <input
                        type="checkbox"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                    />
                    Show archived
                </label>
            </div>

            {attention.length ? (
                <div className="veh-attn">
                    <h4>Needs attention</h4>
                    <ul>
                        {attention.map((a) => (
                            <li key={a.key}>
                                <button type="button" onClick={() => openEditor(a.v)}>
                                    <strong>{a.v.name}</strong>
                                    <span className={`veh-pill ${a.tone}`}>{a.text}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {loading ? (
                <p className="veh-empty">Loading…</p>
            ) : !shown.length ? (
                <p className="veh-empty">
                    No vehicles yet. Add one and it appears on the drivers&rsquo; clock-on screen.
                </p>
            ) : (
                <div className="veh-grid">
                    {shown.map((v) => (
                        <div key={v.id} className={`veh-card${v.is_active ? '' : ' is-archived'}`}>
                            <div className="veh-card-top">
                                <div>
                                    <h4>{v.name}</h4>
                                    {v.plate ? <span className="veh-plate">{v.plate}</span> : null}
                                    {!v.is_active ? <span className="veh-tag">Archived</span> : null}
                                </div>
                            </div>

                            <div className="veh-odo">
                                {v.current_odometer != null ? (
                                    <>
                                        <strong>{Number(v.current_odometer).toLocaleString()} km</strong>
                                        <span>
                                            {v.odometer_driver ? `${v.odometer_driver} · ` : ''}
                                            {v.odometer_at
                                                ? new Date(v.odometer_at).toLocaleDateString('en-GB', {
                                                    day: 'numeric', month: 'short',
                                                })
                                                : ''}
                                            {v.odometer_corrected_at ? ' · corrected' : ''}
                                        </span>
                                    </>
                                ) : (
                                    <span className="veh-noodo">No reading yet</span>
                                )}
                            </div>

                            <dl className="veh-facts">
                                <dt>Mulkiya</dt>
                                <dd>
                                    {v.registration_expiry ? (
                                        <>
                                            <span>{fmtDay(v.registration_expiry)}</span>
                                            <span className={`veh-pill ${dayTone(v.registration_days_left)}`}>
                                                {dayLabel(v.registration_days_left)}
                                            </span>
                                        </>
                                    ) : <span className="veh-unset">Not set</span>}
                                </dd>

                                <dt>Insurance</dt>
                                <dd>
                                    {v.insurance_expiry || v.insurance_provider ? (
                                        <>
                                            <span>
                                                {[v.insurance_provider, fmtDay(v.insurance_expiry)].filter(Boolean).join(' · ')}
                                            </span>
                                            {v.insurance_expiry ? (
                                                <span className={`veh-pill ${dayTone(v.insurance_days_left)}`}>
                                                    {dayLabel(v.insurance_days_left)}
                                                </span>
                                            ) : null}
                                        </>
                                    ) : <span className="veh-unset">Not set</span>}
                                </dd>

                                <dt>Service</dt>
                                <dd>
                                    {v.last_service_date ? (
                                        <>
                                            {v.service_due_km != null ? (
                                                <>
                                                    <span>Next at {km(v.service_due_km)}</span>
                                                    {v.service_km_left != null ? (
                                                        <span className={`veh-pill ${kmTone(v.service_km_left)}`}>
                                                            {kmLabel(v.service_km_left)}
                                                        </span>
                                                    ) : null}
                                                </>
                                            ) : <span className="veh-unset">Meter at service not set</span>}
                                            <small>
                                                Last {fmtDay(v.last_service_date)}
                                                {v.service_interval_km ? ` · ${km(v.service_interval_km)}` : ''}
                                                {v.service_days_left ? ` · about ${v.service_days_left} day${v.service_days_left === 1 ? '' : 's'} left` : ''}
                                            </small>
                                        </>
                                    ) : <span className="veh-unset">Not set</span>}
                                </dd>

                                <dt>Battery</dt>
                                <dd>
                                    {v.battery_changed_on ? (
                                        <span>{fmtDay(v.battery_changed_on)} · {ageLabel(v.battery_changed_on)}</span>
                                    ) : <span className="veh-unset">Not set</span>}
                                </dd>
                            </dl>

                            <div className="veh-docs">
                                {DOCS.filter((d) => v.documents?.[d.type]).map((d) => (
                                    <a key={d.type} href={v.documents[d.type].file_url} target="_blank" rel="noopener noreferrer">
                                        {d.label}
                                    </a>
                                ))}
                                {!v.documents?.mulkiya_front && !v.documents?.mulkiya_back
                                    ? <span className="is-missing">No Mulkiya file</span> : null}
                                {!v.documents?.insurance ? <span className="is-missing">No insurance file</span> : null}
                            </div>

                            {v.note ? <p className="veh-note">{v.note}</p> : null}

                            <div className="veh-actions">
                                <button type="button" onClick={() => openEditor(v)}>Edit</button>
                                {v.odometer_shift_id ? (
                                    <button type="button" onClick={() => openReadings(v)}>
                                        Fix reading
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    className="veh-archive"
                                    onClick={() => setArchived(v, v.is_active)}
                                >
                                    {v.is_active ? 'Archive' : 'Restore'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing ? (
                <div className="veh-modal" onClick={() => !saving && setEditing(null)}>
                    <form className="veh-dialog veh-dialog--form" onClick={(e) => e.stopPropagation()} onSubmit={save}>
                        <h3>{editing.id ? `Edit ${editing.name || 'vehicle'}` : 'Add a vehicle'}</h3>
                        {/* The middle scrolls; the title above and the buttons below stay put. */}
                        <div className="veh-dialog-scroll">
                        <label>
                            <span>Name</span>
                            <input
                                autoFocus={!editing.id}
                                value={editing.name || ''}
                                onChange={(e) => field('name', e.target.value)}
                                placeholder="Toyota Hiace"
                                maxLength={40}
                            />
                        </label>
                        <div className="veh-pair">
                            <label>
                                <span>Plate <em>optional</em></span>
                                <input
                                    value={editing.plate || ''}
                                    onChange={(e) => field('plate', e.target.value)}
                                    placeholder="D 12345"
                                    maxLength={20}
                                />
                            </label>
                            <label>
                                <span>Note <em>optional</em></span>
                                <input
                                    value={editing.note || ''}
                                    onChange={(e) => field('note', e.target.value)}
                                />
                            </label>
                        </div>
                        {editing.id ? (
                            <p className="veh-hint">
                                Renaming carries every recorded shift with it, so the history and
                                the odometer stay attached to this car.
                            </p>
                        ) : (
                            <p className="veh-hint">
                                It appears on the drivers&rsquo; clock-on screen straight away.
                            </p>
                        )}

                        <h5 className="veh-section">Mulkiya and insurance</h5>
                        <div className="veh-pair">
                            <label>
                                <span>Mulkiya expiry</span>
                                <input
                                    type="date"
                                    value={editing.registration_expiry || ''}
                                    onChange={(e) => field('registration_expiry', e.target.value)}
                                />
                            </label>
                            <label>
                                <span>Insurance expiry</span>
                                <input
                                    type="date"
                                    value={editing.insurance_expiry || ''}
                                    onChange={(e) => field('insurance_expiry', e.target.value)}
                                />
                            </label>
                        </div>
                        <label>
                            <span>Insurance provider</span>
                            <input
                                value={editing.insurance_provider || ''}
                                onChange={(e) => field('insurance_provider', e.target.value)}
                                maxLength={80}
                            />
                        </label>
                        <p className="veh-hint">
                            The office is notified 30 days and 7 days before either runs out, and
                            again if it expires.
                        </p>

                        {editing.id ? (
                            <div className="veh-doclist">
                                {DOCS.map((d) => {
                                    const doc = editing.documents?.[d.type];
                                    const st = docStatus[d.type];
                                    return (
                                        <div key={d.type} className="veh-docrow">
                                            <div>
                                                <strong>{d.label}</strong>
                                                {st?.state === 'error' ? (
                                                    <small className="is-error">{st.message}</small>
                                                ) : doc ? (
                                                    <small>
                                                        Uploaded {DateTime.fromISO(String(doc.uploaded_at)).setZone(DUBAI_ZONE).toFormat('d LLL yyyy')}
                                                        {' · '}
                                                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">View</a>
                                                    </small>
                                                ) : (
                                                    <small>Not uploaded</small>
                                                )}
                                            </div>
                                            <input
                                                ref={(el) => { fileInputs.current[d.type] = el; }}
                                                type="file"
                                                accept="image/*,application/pdf"
                                                hidden
                                                onChange={(e) => { upload(d.type, e.target.files?.[0]); e.target.value = ''; }}
                                            />
                                            <button
                                                type="button"
                                                className="veh-upload"
                                                disabled={st?.state === 'uploading'}
                                                onClick={() => fileInputs.current[d.type]?.click()}
                                            >
                                                {st?.state === 'uploading' ? 'Uploading…' : doc ? 'Replace' : 'Upload'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="veh-hint">Add the vehicle first, then upload its Mulkiya and insurance.</p>
                        )}

                        <h5 className="veh-section">Service and battery</h5>
                        <div className="veh-pair">
                            <label>
                                <span>Last service</span>
                                <input
                                    type="date"
                                    max={todayDubaiYMD()}
                                    value={editing.last_service_date || ''}
                                    onChange={(e) => setServiceDate(e.target.value)}
                                />
                            </label>
                            <label>
                                <span>Meter at service</span>
                                <input
                                    inputMode="numeric"
                                    value={editing.last_service_km ?? ''}
                                    onChange={(e) => setEditing((ed) => ({
                                        ...ed, last_service_km: e.target.value.replace(/\D/g, ''), kmTyped: true, meterSource: null,
                                    }))}
                                    placeholder="km"
                                    disabled={!editing.last_service_date}
                                />
                            </label>
                        </div>
                        <div className="veh-field">
                            <span>Service type</span>
                            <div className="veh-seg" role="radiogroup" aria-label="Service type">
                                {[5000, 10000].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        role="radio"
                                        aria-checked={Number(editing.service_interval_km) === n}
                                        className={Number(editing.service_interval_km) === n ? 'is-on' : ''}
                                        disabled={!editing.last_service_date}
                                        onClick={() => field('service_interval_km', n)}
                                    >
                                        {km(n)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <p className="veh-hint">
                            {editing.meterSource === 'clock_on'
                                ? 'Meter filled from that day’s clock-on. Change it if the service sticker says otherwise. '
                                : editing.meterSource === 'last_reading'
                                    ? 'Meter filled from the last reading before that day. Change it if the service sticker says otherwise. '
                                    : ''}
                            The next service is due at the meter at service plus the service type.
                            The office is notified 1,000 km before, and again when it is due.
                        </p>
                        <label className="veh-half">
                            <span>Last battery change</span>
                            <input
                                type="date"
                                max={todayDubaiYMD()}
                                value={editing.battery_changed_on || ''}
                                onChange={(e) => field('battery_changed_on', e.target.value)}
                            />
                        </label>
                        </div>
                        {formError ? <div className="veh-formError">{formError}</div> : null}
                        <div className="veh-dialog-actions">
                            <button type="button" onClick={() => setEditing(null)} disabled={saving}>
                                Cancel
                            </button>
                            <button type="submit" className="primary" disabled={saving || !editing.name?.trim()}>
                                {saving ? 'Saving…' : editing.id ? 'Save' : 'Add'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            {reading ? (
                <div className="veh-modal" onClick={() => !saving && setReading(null)}>
                    <div className="veh-dialog veh-dialog--wide" onClick={(e) => e.stopPropagation()}>
                        <h3>Correct the meter · {reading.vehicle}</h3>
                        {/* The middle scrolls; the title above and Close below stay put. */}
                        <div className="veh-dialog-scroll">
                        <p className="veh-hint">
                            Every recent clock-on and clock-off, newest first. Change the wrong
                            number and save that row — the day&rsquo;s distance fixes itself, the
                            original is kept with your name. A typo at clock-off usually gets copied
                            into the next morning&rsquo;s clock-on, so correct the <strong>earliest</strong> wrong
                            number first and work forward.
                        </p>
                        {reading.shifts.length === 0 ? (
                            <p className="veh-empty">No shifts recorded for this vehicle yet.</p>
                        ) : (
                            <div className="veh-readings">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Day</th>
                                            <th>Driver</th>
                                            <th>Clock-on</th>
                                            <th>Clock-off</th>
                                            <th>Driven</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reading.shifts.map((s) => {
                                            const e = reading.edits[s.id] || { start: '', end: '' };
                                            const km = s.end_odometer != null && s.start_odometer != null
                                                ? Number(s.end_odometer) - Number(s.start_odometer) : null;
                                            const dirty = rowDirty(s);
                                            const err = reading.rowError?.[s.id];
                                            return (
                                                <React.Fragment key={s.id}>
                                                    <tr className={dirty ? 'is-dirty' : ''}>
                                                        <td data-label="Day">{dayOf(s.started_at)}</td>
                                                        <td data-label="Driver">{s.driver || '—'}</td>
                                                        <td data-label="Clock-on">
                                                            <input
                                                                inputMode="numeric"
                                                                value={e.start}
                                                                onChange={(ev) => editRow(s.id, 'start', ev.target.value)}
                                                                disabled={saving}
                                                            />
                                                            {s.start_odometer_was != null && (
                                                                <small>was {Number(s.start_odometer_was).toLocaleString()}</small>
                                                            )}
                                                        </td>
                                                        <td data-label="Clock-off">
                                                            {s.end_odometer != null ? (
                                                                <>
                                                                    <input
                                                                        inputMode="numeric"
                                                                        value={e.end}
                                                                        onChange={(ev) => editRow(s.id, 'end', ev.target.value)}
                                                                        disabled={saving}
                                                                    />
                                                                    {s.end_odometer_was != null && (
                                                                        <small>was {Number(s.end_odometer_was).toLocaleString()}</small>
                                                                    )}
                                                                </>
                                                            ) : <span className="veh-open">still out</span>}
                                                        </td>
                                                        <td className="veh-km" data-label="Driven">{km != null ? `${km.toLocaleString()} km` : '—'}</td>
                                                        <td className="veh-save">
                                                            <button
                                                                type="button"
                                                                className="primary"
                                                                onClick={() => saveRow(s)}
                                                                disabled={saving || !dirty}
                                                            >
                                                                {saving ? '…' : 'Save'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {err ? (
                                                        <tr className="veh-rowError"><td colSpan={6}>{err}</td></tr>
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        </div>
                        <div className="veh-dialog-actions">
                            <button type="button" onClick={() => setReading(null)} disabled={saving}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
